import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import type { ShipmentApi } from "@services/shipments/types";
import { getShipmentDetailsFromProperties } from "./shipmentPropertiesUtils";
import type { ShipmentUpdate, EmailSource } from "./timelineWithThreadLinesComponent/types";

export type { ShipmentUpdate, EmailSource };

export interface ShipmentDetailsProps {
  shipment: ShipmentApi;
  maxVisibleDetails?: number;
  updates?: ShipmentUpdate[]; // Updates to highlight
  emailSource?: EmailSource; // Email metadata to display
  hideTitle?: boolean; // Hide the "Shipment Details" title
}

export const ShipmentDetails: React.FC<ShipmentDetailsProps> = ({
  shipment,
  maxVisibleDetails = 6,
  updates = [],
  emailSource,
  hideTitle = false,
}) => {
  let details = getShipmentDetailsFromProperties(shipment);

  // Auto-expand when there are updates, otherwise start collapsed
  const [showAll, setShowAll] = useState(updates.length > 0);

  // Update showAll when updates prop changes
  useEffect(() => {
    if (updates.length > 0) {
      setShowAll(true);
    } else {
      setShowAll(false);
    }
  }, [updates.length]);

  // Create a map of property names to their updates
  const updatesByProperty = new Map<string, ShipmentUpdate>();
  updates.forEach((update) => {
    updatesByProperty.set(update.propertyName, update);
  });

  // Add any added properties that aren't already in the details
  const existingPropertyNames = new Set<string>(
    details.flatMap((d) => d.propertyNames)
  );

  const addedUpdates = updates.filter(
    (u) =>
      u.updateType === "addedProperty" &&
      !existingPropertyNames.has(u.propertyName)
  );

  if (addedUpdates.length > 0) {
    const addedDetails = addedUpdates.map((update) => ({
      label: update.propertyName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      propertyNames: [update.propertyName] as any,
      values: update.newValue ? [update.newValue] : [],
      displayValue: update.newValue || "",
    }));
    details = [...details, ...addedDetails];
  }

  const visibleDetails = showAll
    ? details
    : details.slice(0, maxVisibleDetails);
  const hasMoreDetails = details.length > maxVisibleDetails;

  // Helper to render a property value with update indicators
  const renderPropertyValue = (
    propertyNames: string[],
    displayValue: string
  ) => {
    // Check if any of the property names has an update
    const update = propertyNames
      .map((name) => updatesByProperty.get(name))
      .find((u) => u !== undefined);

    if (!update) {
      return (
        <Typography variant="body2" sx={{ color: "text.primary" }}>
          {displayValue || "-"}
        </Typography>
      );
    }

    // Added property
    if (update.updateType === "addedProperty") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: "success.main",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {update.newValue || displayValue || "-"}
          </Typography>
          <Chip
            label="Added"
            size="small"
            color="success"
            sx={{ height: 18, fontSize: "0.7rem" }}
          />
        </Box>
      );
    }

    // Modified property
    if (update.updateType === "modifiedProperty") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.disabled",
              textDecoration: "line-through",
              fontSize: "0.813rem",
            }}
          >
            {update.oldValue || "-"}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography
            variant="body2"
            sx={{ color: "info.main", fontWeight: 600 }}
          >
            {update.newValue || displayValue || "-"}
          </Typography>
          <Chip
            label="Modified"
            size="small"
            color="info"
            sx={{ height: 18, fontSize: "0.7rem" }}
          />
        </Box>
      );
    }

    // Removed property
    if (update.updateType === "removedProperty") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.disabled",
              textDecoration: "line-through",
            }}
          >
            {update.oldValue || displayValue || "-"}
          </Typography>
          <Chip
            label="Removed"
            size="small"
            color="error"
            sx={{ height: 18, fontSize: "0.7rem" }}
          />
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: "text.primary" }}>
        {displayValue || "-"}
      </Typography>
    );
  };

  return (
    <Box
      sx={{ mb: 3, p: 0, border: 0, borderColor: "divider", borderRadius: 1 }}
    >
      {!hideTitle && (
        <Typography variant="h6" gutterBottom>
          Shipment Details
        </Typography>
      )}

      {/* Email source information */}
      {emailSource && emailSource.subject && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "action.hover",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            Updates from email:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {emailSource.subject}
          </Typography>
          {emailSource.from && (
            <Typography variant="caption" color="text.secondary">
              From: {emailSource.from}
            </Typography>
          )}
        </Box>
      )}

      {/* Always visible details */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {visibleDetails.map((detail) => (
          <Box key={detail.label} sx={{ display: "flex", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, minWidth: 140, color: "text.secondary" }}
            >
              {detail.label}:
            </Typography>
            {renderPropertyValue(detail.propertyNames, detail.displayValue)}
          </Box>
        ))}
      </Box>

      {/* Accordion for additional details if more than maxVisibleDetails */}
      {hasMoreDetails && !showAll && (
        <Accordion
          sx={{ mt: 1, boxShadow: "none", border: "none" }}
          onChange={() => setShowAll(true)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 0,
              flexDirection: "row-reverse",
              "& .MuiAccordionSummary-content": {
                margin: "8px 0",
                marginLeft: 1,
              },
              "& .MuiAccordionSummary-expandIconWrapper": {
                marginRight: 0,
              },
            }}
          >
            <Typography variant="body2" color="primary">
              Show {details.length - maxVisibleDetails} more details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {details.slice(maxVisibleDetails).map((detail) => (
                <Box key={detail.label} sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      minWidth: 140,
                      color: "text.secondary",
                    }}
                  >
                    {detail.label}:
                  </Typography>
                  {renderPropertyValue(
                    detail.propertyNames,
                    detail.displayValue
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Collapse option when showing all */}
      {hasMoreDetails && showAll && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: "pointer" }}
            onClick={() => setShowAll(false)}
          >
            Show less
          </Typography>
        </Box>
      )}
    </Box>
  );
};
